import React, { Component } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash.noop'
import { Accordion, Button, ProgressBar } from '@sainsburys-tech/bolt'
import equal from 'deep-equal'
import { InView } from 'react-intersection-observer'

import CustomerQuestionsHeader, {
  ModalQuestions,
  AccordionHeader,
  ErrorMessage,
  QandANoQuestion
} from './QandAComponents'
import QuestionItem from '../QuestionItem/QuestionItem'
import { pushEventAndTrack } from '../../helpers/taggingSerializer'
import { scrollToAccordion } from '../../helpers/openAccordion'
import { runBVQuestionsTrack } from '../../helpers/bazaarVoiceTracking'
import sortOptionList from './sortOptions.json'
import * as Styled from './QandA.style'

export class QandA extends Component {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      sortOrder: 'SubmissionTime:Desc',
      questionOffset: 0,
      loadQandA: false,
      searchTerm: ''
    }

    this.scrollTop = 0
  }

  componentDidMount() {
    runBVQuestionsTrack('Passive', this.props)
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.paginatedQuestion &&
      this.isPropsArrayDifferent(prevProps.paginatedQuestion, this.props.paginatedQuestion)
    ) {
      this.setState({ loading: false })

      const scrollArea = document.querySelector('.ac-drawer__content') || { scrollTop: 0 }
      scrollArea.scrollTop = 0
    }
  }

  shouldComponentUpdate = (nextProps, nextState) => !equal(this.props, nextProps) || !equal(this.state, nextState)

  onAccordionInteraction = (index, show) => {
    this.props.onAccordionChange('qanda', show)

    if (show) scrollToAccordion('#qanda', this.props.isMadeToOrder)

    // Adobe Analytics Tracking Code
    const adobeAnalytics = show ? pushEventAndTrack : noop
    adobeAnalytics({
      eventInfo: {
        eventName: 'pdp_showMore',
        eventAction: 'Questions and Answers'
      }
    })

    runBVQuestionsTrack(
      'FeaturedEvent',
      this.props,
      'ReadQuestions',
      'pdp_showMoreShowLess',
      show ? 'Show More' : 'Show Less'
    )
  }

  onSortChange = event => {
    event.preventDefault()
    const { value: sortOrder } = event.target
    this.setState({ sortOrder, questionOffset: 0 }, () => this.fetchQuestionsAnswers())
    runBVQuestionsTrack('FeaturedEvent', this.props, 'sort', 'pdp_questions', `sort:${sortOrder}`)
  }

  onSubmitSearch = () => {
    this.setState({ questionOffset: 0 }, () => this.fetchQuestionsAnswers())
    pushEventAndTrack(
      {
        eventInfo: {
          eventName: 'pdp_qandaSearch',
          eventAction: 'Q and A Search'
        },
        attributes: {
          searchTerm: this.state.searchTerm
        }
      },
      'pdp_qandaSearch'
    )
    runBVQuestionsTrack('FeaturedEvent', this.props, 'search', 'pdp_questions', `searchTerm:${this.state.searchTerm}`)
  }

  onSearchTermChange = event => {
    this.setState({ searchTerm: event.target.value }, () => !this.state.searchTerm && this.fetchQuestionsAnswers())
  }

  onInvalidSearch = () => {
    this.setState({ searchTerm: '' }, () => this.fetchQuestionsAnswers())
  }

  showMoreQuestions = () => {
    const { questionOffset } = this.state
    const nextQuestionOffset = questionOffset + 10

    this.setState(
      {
        questionOffset: nextQuestionOffset,
        loading: true
      },
      () => this.fetchQuestionsAnswers()
    )

    // Adobe Analytics Tracking Code
    pushEventAndTrack(
      {
        eventInfo: {
          eventName: 'pdp_moreQuestions',
          eventAction: 'Click on More Questions'
        }
      },
      'pdp_moreQuestions'
    )
    // BazaarVoice Analytics Tracking Code
    runBVQuestionsTrack('FeaturedEvent', this.props, 'MoreQuestions', 'pdp_moreQuestions', 'Click on More Questions')
  }

  fetchQuestionsAnswers = () => {
    const { requestBVQuestionAnswerData, partNumber } = this.props
    const { sortOrder, questionOffset, searchTerm } = this.state

    requestBVQuestionAnswerData({
      partNumber,
      searchTerm,
      query: {
        Limit: 10,
        Offset: questionOffset,
        Sort: sortOrder,
        Search: searchTerm || undefined
      }
    })
  }

  isPropsArrayDifferent = (prevPropArr, nextPropArr) => {
    if (prevPropArr.length !== nextPropArr.length) return true

    for (let index = 0; index < prevPropArr.length; index += 1) {
      if (prevPropArr[index] !== nextPropArr[index]) return true
    }
    return false
  }

  render() {
    const {
      props,
      state,
      onSortChange,
      onSubmitSearch,
      onSearchTermChange,
      onAccordionInteraction,
      onInvalidSearch
    } = this
    const { sortOrder, loading, searchTerm } = state
    const { hasError, stats, partNumber, paginatedQuestion } = props
    const { questionCount = 0, answerCount = 0 } = stats.attributes || {}
    const headingText = 'Questions and answers'
    const questionItems = paginatedQuestion.map((question, key) => (
      <QuestionItem key={key} {...{ partNumber, question, index: key }} />
    ))

    const progressBarFillPercentage = paginatedQuestion
      ? Math.floor((paginatedQuestion.length / questionCount) * 100)
      : 0
    const Header = <AccordionHeader {...{ questionCount, answerCount, headingText }} />
    const accordionOptions = {
      title: Header,
      name: 'accordion-qa',
      onClick: onAccordionInteraction,
      id: 'qanda',
      'data-test': 'qa-accordion',
      index: 'qa-accordion'
    }

    const BZAnalyticsData = {
      breadcrumb: props.breadcrumb,
      attributes: props.attributes,
      partNumber,
      stats: props.stats
    }

    return (
      <Accordion.Item as='h3' hasBorderBottom {...accordionOptions} noAnimation>
        {this.props.questions.length < 1 ? (
          <QandANoQuestion partNumber={partNumber} BZAnalyticsData={BZAnalyticsData} />
        ) : (
          <InView
            triggerOnce
            rootMargin='0px 0px 400px 0px'
            onChange={inView => inView && this.setState({ loadQandA: true })}
          >
            {this.state.loadQandA && (
              <>
                <CustomerQuestionsHeader
                  {...{
                    searchTerm,
                    sortOrder,
                    sortOptions: sortOptionList,
                    onSortChange,
                    onSubmitSearch,
                    onSearchTermChange,
                    partNumber,
                    BZAnalyticsData
                  }}
                />

                <ErrorMessage {...{ hasError, onClick: onInvalidSearch, searchTerm }} />
                <ModalQuestions {...{ hasError, questionItems }} />

                <Styled.MoreQuestionsButtonContainer data-test='show-more-questions-button'>
                  {!hasError && (
                    <>
                      <Styled.MoreQuestionsButtonText data-test='show-more-questions-button-info-text'>{`You've viewed ${paginatedQuestion.length} of ${questionCount} questions`}</Styled.MoreQuestionsButtonText>
                      <Styled.ProgressBarContainer>
                        <ProgressBar
                          value={progressBarFillPercentage}
                          scale={100}
                          height={3}
                          color='color_secondary'
                          showCount={false}
                          data-test='viewed-x-of-y-questions-progress-bar'
                        />
                      </Styled.ProgressBarContainer>

                      {questionCount > paginatedQuestion.length && (
                        <Button
                          kind='secondary'
                          onClick={this.showMoreQuestions}
                          onKeyPress={this.showMoreQuestions}
                          tabIndex='0'
                          isLoading={loading}
                          data-test='show-x-more-questions-button'
                        >
                          {questionCount - paginatedQuestion.length >= 10
                            ? 'Show 10 more questions'
                            : `Show ${questionCount - paginatedQuestion.length} more questions`}
                        </Button>
                      )}
                    </>
                  )}
                </Styled.MoreQuestionsButtonContainer>
              </>
            )}
          </InView>
        )}
      </Accordion.Item>
    )
  }
}

QandA.propTypes = {
  searchTerm: PropTypes.string,
  hasError: PropTypes.bool,
  paginatedQuestion: PropTypes.array,
  stats: PropTypes.shape({
    attributes: PropTypes.shape({
      questionCount: PropTypes.number,
      answerCount: PropTypes.number
    })
  }),
  questions: PropTypes.array.isRequired,
  partNumber: PropTypes.string.isRequired,
  onAccordionChange: PropTypes.func.isRequired
}
QandA.defaultProps = {
  hasError: false,
  paginatedQuestion: [],
  stats: {
    attributes: {
      questionCount: 0,
      answerCount: 0
    }
  }
}
export default QandA
